import { Component, Input, SimpleChanges, Inject } from '@angular/core';
import { MixpanelService } from 'src/app/core/services/mixpanel.service';
import { FiltersService } from 'src/app/core/services/filters.service';
import { Subscription } from 'rxjs';
import { ContractModel } from 'src/app/global/contract-filter-model';
import { SPLocationExpandedProduct } from '../../types/solution-participation-types';
import { FEATURE_FLAGS, FeatureFlags } from 'src/app/config/AppInitializer.factory';

@Component({
    selector: 'app-location-product-details-table',
    templateUrl: './location-product-details-table.component.html',
    styleUrls: ['./location-product-details-table.component.scss'],
    standalone: false
})
export class LocationProductDetailsTableComponent {
  @Input() siteId: string = '';
  @Input() productData: any;
  @Input() solutionGroup: string = '';
  @Input() siteName: string = '';

  // Determines if this is opened from the right section of location view and disables scroll + triggers mixpanel
  @Input() rightSection: boolean = false;

  sortedProductData: any;
  filters: ContractModel | undefined;
  filtersSubscription: Subscription | undefined;

  // Flag to hide Rebate, MPL and SPL columns
  hideRebateMplSplColumns: boolean = false;

  //Used to determine if the region/s selected is/are non-NA to hide radios
  hasOnlyNonNARegions: boolean = false;

  hasMultipleRegions: boolean = false;

  get isRefreshedSolutionParticipationUIEnabled(): boolean {
    return this._featureFlags.enableRefreshedSolutionParticipationUI;
  }

  constructor(
    private _mixpanelService : MixpanelService,
    private _filterService: FiltersService,
    @Inject(FEATURE_FLAGS) private _featureFlags: FeatureFlags
  ) {

  }

  ngOnInit() {
    // If modal is opened from right-section disable scroll.
    // If its opened from left-section, scroll is already disabled by parent modal component.
    this.filtersSubscription = this._filterService.selectedFiltersObservable.subscribe(f => this.filters = f);

    if(this.filters != undefined) {
      //Check if the selected region/s is non-NA to determine if we need to hide the radios
      this.hasOnlyNonNARegions = this.filters?.regions ? !this.filters.regions.includes('NA') : false;
      this.hasMultipleRegions = (this.filters?.regions?.length ?? 0) > 1;

      // Set hideRebateMplSplColumns to true if the selected division is FRS or QSR
      this.hideRebateMplSplColumns = this.filters?.division === 'FRS' || this.filters?.division === 'QSR' || this.hasOnlyNonNARegions || this.hasMultipleRegions;
    }

    if(this.rightSection) {
      document.body.style.overflow = 'hidden';
    }

    if (this.productData) {
      this.sortData();
    }

    // Mixpanel specifically for the individual solution group table opened from the right section of location view
    if(this.rightSection) {
      this._mixpanelService.track('Opened Solution Group with Products Modal', {
        division: this.filters?.division,
        dataView: this.filters?.dataView,
        regionSelected: this.filters?.regions,
        countriesSelected: this.filters?.countries,
        H5s: this.filters?.topLevelContractGroup,
        H7s: this.filters?.secondLevelContractFilter,
        acctsSelected: this.filters?.accts,
        periodSelected: this.filters?.period,
        "Custom Date Range": this.filters?.dateRange,
        siteId: this.siteId,
        siteName: this.siteName,
        solutionGroup: this.solutionGroup
      });
    }
  }

  ngOnChanges(changes: SimpleChanges){
    if (changes['productData'] && this.productData) {
      this.sortData();
    }
  }

  sortData() {
    // Make a deep copy of the incoming data
    this.sortedProductData = JSON.parse(JSON.stringify(this.productData));

    // Sort by lastPurchased date (earliest first), '-' should come last
    this.sortedProductData?.sort((a: any, b: any) => {
      if (a.lastPurchased === '-' && b.lastPurchased !== '-') {
        return 1;
      } else if (a.lastPurchased !== '-' && b.lastPurchased === '-') {
        return -1;
      } else if (a.lastPurchased === '-' && b.lastPurchased === '-') {
        return 0;
      } else {
        return new Date(b.lastPurchased).getTime() - new Date(a.lastPurchased).getTime();
      }
    });

    // Convert to date format MM-YYYY
    this.sortedProductData?.forEach((product: any) => {
      if (product.lastPurchased !== '-') {
        const dateParts = product.lastPurchased.split('-');
        product.lastPurchased = dateParts[1] + '-' + dateParts[0];
      }
    });
  }

  ngOnDestroy() {
    if(this.rightSection) {
      document.body.style.overflow = 'auto';
    }

    if(this.filtersSubscription) {
      this.filtersSubscription.unsubscribe();
    }
  }
}
